class TrFaqTabs extends HTMLElement {
	selectors = {
		tab: '[data-tr-js-faq-tabs-tab]',
		activeTitle: '[data-tr-js-faq-tabs-active-title]',
		panel: '[data-tr-js-faq-tabs-panel]',
		item: '[data-tr-js-faq-tabs-item]',
	};

	attributes = {};

	stateClasses = {
		isActive: 'is-active',
	};

	connectedCallback() {
		this.tabElements = [...this.querySelectorAll(this.selectors.tab)];
		this.activeTitleElement = this.querySelector(this.selectors.activeTitle);
		this.panelElements = [...this.querySelectorAll(this.selectors.panel)];
		this.itemElements = [...this.querySelectorAll(this.selectors.item)];

		this.init();
		this.bindEvents();

		document.addEventListener('shopify:section:load', this.onSectionLoad);
		document.addEventListener('shopify:block:select', this.onBlockSelect);
	}

	disconnectedCallback() {
		this.destroy();

		document.removeEventListener('shopify:section:load', this.onSectionLoad);
		document.removeEventListener('shopify:block:select', this.onBlockSelect);
	}

	init() {
		const activeTabElement =
			this.tabElements.find(tabElement =>
				tabElement.classList.contains(this.stateClasses.isActive),
			) || this.tabElements[0];

		if (!activeTabElement) return;

		this.setActiveTab(activeTabElement);
	}

	bindEvents() {
		this.tabElements.forEach(tabElement => {
			tabElement.addEventListener('click', this.onTabClick);
			tabElement.addEventListener('keydown', this.onTabKeydown);
		});
	}

	onSectionLoad = event => {
		if (
			!this.closest(`#${event.detail.sectionId}`) &&
			!this.closest(`#shopify-section-${event.detail.sectionId}`)
		)
			return;

		this.destroy();

		this.tabElements = [...this.querySelectorAll(this.selectors.tab)];
		this.activeTitleElement = this.querySelector(this.selectors.activeTitle);
		this.panelElements = [...this.querySelectorAll(this.selectors.panel)];
		this.itemElements = [...this.querySelectorAll(this.selectors.item)];

		this.init();
		this.bindEvents();
	};

	onBlockSelect = event => {
		const selectedTabElement = this.tabElements.find(
			tabElement => tabElement.dataset.trJsFaqTabsTab === event.detail.blockId,
		);

		if (selectedTabElement) {
			this.setActiveTab(selectedTabElement);

			return;
		}

		const selectedItemElement = this.itemElements.find(
			itemElement => itemElement.dataset.trJsFaqTabsItem === event.detail.blockId,
		);

		if (!selectedItemElement) return;

		const relatedTabElement = this.tabElements.find(
			tabElement =>
				tabElement.dataset.trJsFaqTabsHandle ===
				selectedItemElement.dataset.trJsFaqTabsItemHandle,
		);

		if (!relatedTabElement) return;

		this.setActiveTab(relatedTabElement);
	};

	onTabClick = event => {
		this.setActiveTab(event.currentTarget);
	};

	onTabKeydown = event => {
		if (
			event.key !== 'ArrowDown' &&
			event.key !== 'ArrowRight' &&
			event.key !== 'ArrowUp' &&
			event.key !== 'ArrowLeft'
		)
			return;

		event.preventDefault();

		const currentIndex = this.tabElements.indexOf(event.currentTarget);
		const nextIndex =
			event.key === 'ArrowDown' || event.key === 'ArrowRight'
				? currentIndex + 1
				: currentIndex - 1;
		const boundedIndex =
			(nextIndex + this.tabElements.length) % this.tabElements.length;
		const nextTabElement = this.tabElements[boundedIndex];

		nextTabElement.focus();
		this.setActiveTab(nextTabElement);
	};

	setActiveTab(activeTabElement) {
		const activeTabId = activeTabElement.dataset.trJsFaqTabsTab;
		const activeTabTitle = activeTabElement.dataset.trJsFaqTabsTitle;

		if (this.activeTitleElement && activeTabTitle) {
			this.activeTitleElement.textContent = activeTabTitle;
		}

		this.tabElements.forEach(tabElement => {
			const isActive = tabElement === activeTabElement;

			tabElement.classList.toggle(this.stateClasses.isActive, isActive);
			tabElement.setAttribute('aria-selected', isActive ? 'true' : 'false');
			tabElement.setAttribute('tabindex', isActive ? '0' : '-1');
		});

		this.panelElements.forEach(panelElement => {
			const isActive = panelElement.dataset.trJsFaqTabsPanel === activeTabId;

			panelElement.classList.toggle(this.stateClasses.isActive, isActive);
			panelElement.hidden = !isActive;
		});
	}

	destroy() {
		this.tabElements.forEach(tabElement => {
			tabElement.removeEventListener('click', this.onTabClick);
			tabElement.removeEventListener('keydown', this.onTabKeydown);
		});
	}
}

if (!customElements.get('tr-faq-tabs')) {
	customElements.define('tr-faq-tabs', TrFaqTabs);
}
